import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import update from 'immutability-helper';
import { faMinus, faPlus } from '@fortawesome/free-solid-svg-icons';
import { useSelector } from 'react-redux';

import AdminPageContainer from '../common/AdminPageContainer';

import { BackLink, SaveButton, IconButton } from '../common/Buttons';

import Loader from '../../common/loader/Loader';
import Alert from '../../common/Alert';
import { checkUserAuth } from '../../../utils/auth-utils';

const CourseProgressCounter = props => {
  const dec = () => {
    if (props.value > 0) {
      props.onChange(props.value - 1);
    }
  };

  const inc = () => {
    if (props.value < props.total) {
      props.onChange(props.value + 1);
    }
  };

  return (
    <div className="is-flex align-items-center">
      <span>Progress:</span>
      <IconButton
        className="button is-small is-link ml-10px"
        icon={faMinus}
        onClick={dec}
        disabled={props.value === 0}
      />
      <span className="mx-10px">
        {props.value} of {props.total}
      </span>
      <IconButton
        className="button is-small is-link"
        icon={faPlus}
        onClick={inc}
        disabled={props.value === props.total}
      />
    </div>
  );
};

CourseProgressCounter.propTypes = {
  value: PropTypes.number.isRequired,
  total: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired,
};

const AdminUserCoursesPage = props => {
  const { token } = useSelector(s => s);

  const { userId } = props.match.params;

  const [courses, setCourses] = useState([]);

  const [status, setStatus] = useState({ isLoading: false, isError: false });

  useEffect(() => {
    setStatus({ isLoading: true, isError: false });
    axios
      .get(`/api/v1/admin/users/${userId}/courses`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then(response => {
        setCourses(response.data.courses);
        setStatus({ isLoading: false, isError: false });
      })
      .catch(e => {
        setStatus({ isLoading: false, isError: true });
        checkUserAuth(e, props.history);
      });
  }, []);

  const changeIsSelected = courseId => e => {
    const index = courses.findIndex(x => x.courseId === courseId);

    setCourses(update(courses, { [index]: { isSelected: { $set: e.target.checked } } }));
  };

  const changeProgress = courseId => value => {
    const index = courses.findIndex(x => x.courseId === courseId);

    setCourses(update(courses, { [index]: { webinarsPassed: { $set: value } } }));
  };

  const saveChanges = () => {
    const selectedCourses = courses.filter(x => x.isSelected);
    setStatus({ isLoading: true, isError: false });

    axios
      .put(
        `/api/v1/admin/users/${userId}/courses`,
        {
          courses: selectedCourses.map(({ courseId, webinarsPassed }) => ({ courseId, webinarsPassed })),
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      )
      .then(() => {
        setStatus({ isLoading: false, isError: false });
        props.history.push('/admin/users');
      })
      .catch(e => {
        setStatus({ isLoading: false, isError: true });
        checkUserAuth(e, props.history);
      });
  };

  return (
    <AdminPageContainer>
      <Loader isVisible={status.isLoading} />
      {status.isError && <Alert isError message="An unknown error occured" />}
      <BackLink to="/admin/users" />
      <SaveButton className="ml-20px" onClick={saveChanges} />
      <div className="py-20px">
        {courses.map(course => (
          <div key={course.courseId} className="admin-table-row">
            <div className="field">
              <label className="checkbox">
                <input type="checkbox" checked={course.isSelected} onChange={changeIsSelected(course.courseId)} />
                <span className="ml-20px">{course.name}</span>
              </label>
            </div>

            {course.isSelected && (
              <CourseProgressCounter
                value={course.webinarsPassed}
                total={course.webinarsCount}
                onChange={changeProgress(course.courseId)}
              />
            )}
          </div>
        ))}
      </div>
    </AdminPageContainer>
  );
};

AdminUserCoursesPage.propTypes = {
  history: PropTypes.object.isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape({
      userId: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
};

export default AdminUserCoursesPage;
